import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Landmark, Pencil, Globe, Phone, Mail, MapPin, Printer, Quote, Eye, IdCard } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { printCompanyProfile, printVisitingCard } from "@/lib/print";
import { toast } from "sonner";

const Row = ({ label, value, testid }) => (
  <div className="flex flex-wrap gap-2 border-b border-border/60 py-2 last:border-b-0">
    <span className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    <span className="text-sm font-medium" data-testid={testid}>{value || "—"}</span>
  </div>
);

const F = ({ k, label, textarea, form, setForm }) => (
  <div className="grid gap-1.5">
    <Label>{label}</Label>
    {textarea ? (
      <Textarea value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`profile-field-${k}`} />
    ) : (
      <Input value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`profile-field-${k}`} />
    )}
  </div>
);

export default function NTCProfile() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [profile, setProfile] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [letterhead, setLetterhead] = useState(localStorage.getItem("ntc_letterhead_mode") === "1");

  const load = () =>
    api.get("/company-profile").then((r) => {
      setProfile(r.data);
      try {
        localStorage.setItem("ntc_branch_addresses", JSON.stringify(r.data?.branch_addresses || {}));
        localStorage.setItem("ntc_signature_image", r.data?.signature_image || "");
        localStorage.setItem("ntc_seal_image", r.data?.seal_image || "");
        if (r.data?.regional_office) localStorage.setItem("ntc_regional_office", r.data.regional_office);
      } catch {}
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openEdit = () => {
    setForm({ ...profile, branches: (profile.branches || []).join(", ") });
    setDialogOpen(true);
  };

  const uploadStamp = (field) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — keep it under 8MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    toast.info("Processing scan — cropping the stamp…");
    api.post("/company-profile/prepare-stamp", fd)
      .then((r) => {
        setForm((f) => ({ ...f, [field]: r.data.image }));
        toast.success("Stamp ready — click Save to apply");
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not process the file"));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, branches: String(form.branches || "").split(",").map((b) => b.trim()).filter(Boolean) };
      await api.put("/company-profile", payload);
      toast.success("Profile updated");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="py-20 text-center text-sm text-muted-foreground">Loading profile…</div>;

  return (
    <div>
      <PageHeader
        title="NTC Profile"
        subtitle="Company profile — details used across the suite."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => printVisitingCard(profile)} data-testid="print-visiting-card-btn">
              <IdCard className="mr-2 h-4 w-4" /> Visiting Card
            </Button>
            <Button variant="outline" onClick={() => printCompanyProfile(profile)} data-testid="print-profile-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            {isAdmin && (
              <Button onClick={openEdit} data-testid="edit-profile-btn">
                <Pencil className="mr-2 h-4 w-4" /> Edit Profile
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-6 flex items-center gap-4 rounded-lg border border-border bg-card p-5" data-testid="profile-hero">
        <img src="/ntc-logo.jpg" alt="NTC logo" className="h-16 w-16 rounded-md bg-white object-contain" />
        <div>
          <div className="font-display text-xl font-bold">{profile.name}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{profile.phone}</span>
            <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{profile.email}</span>
            <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{profile.website}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4 text-primary" /> Company Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Row label="Name" value={profile.name} testid="profile-name" />
            <Row label="Regional Office" value={profile.regional_office || "South"} testid="profile-regional-office" />
            <Row label="Opening Date" value={profile.opening_date ? fmtDate(profile.opening_date) : "—"} testid="profile-opening-date" />
            <Row label="Managing Director" value={profile.md_name} testid="profile-md-name" />
            <Row label="MD Mobile No." value={profile.md_mobile} testid="profile-md-mobile" />
            <Row label="GSTIN" value={profile.gstin} testid="profile-gstin" />
            <Row label="PAN" value={profile.pan} testid="profile-pan" />
            <Row label="MSME Number" value={profile.msme_number} testid="profile-msme" />
            <Row label="Address" value={profile.address} testid="profile-address" />
            <div className="flex flex-wrap gap-2 py-2">
              <span className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Branches</span>
              <span className="flex flex-wrap gap-1.5" data-testid="profile-branches">
                {(profile.branches || []).map((b) => (
                  <Badge key={b} variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">{b}</Badge>
                ))}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Landmark className="h-4 w-4 text-primary" /> Bank Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Row label="Bank" value={profile.bank_name} testid="profile-bank" />
            <Row label="A/C No." value={profile.account_no} testid="profile-account" />
            <Row label="IFSC" value={profile.ifsc} testid="profile-ifsc" />
            <Row label="Phone" value={profile.phone} testid="profile-phone" />
            <Row label="Email" value={profile.email} testid="profile-email" />
            <Row label="Website" value={profile.website} testid="profile-website" />
            <Row label="Fleet Manager" value={profile.fleet_manager_name} testid="profile-fleet-manager-name" />
            <Row label="Fleet Mgr WhatsApp" value={profile.fleet_manager_whatsapp} testid="profile-fleet-manager-whatsapp" />
          </CardContent>
        </Card>
      </div>

      {Object.values(profile.branch_addresses || {}).some((v) => String(v || "").trim()) && (
        <Card className="mt-6" data-testid="branch-addresses-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4 text-primary" /> Branch Address Book</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(profile.branch_addresses || {}).filter(([, v]) => String(v || "").trim()).map(([b, v]) => (
              <Row key={b} label={`${b} Branch`} value={v} testid={`branch-address-row-${b.toLowerCase()}`} />
            ))}
            <p className="mt-2 text-xs text-muted-foreground">These addresses print on that branch's consignment notes, challans and reminder letters.</p>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6" data-testid="letterhead-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Printer className="h-4 w-4 text-primary" /> Letterhead Mode</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={letterhead}
              onChange={(e) => {
                setLetterhead(e.target.checked);
                localStorage.setItem("ntc_letterhead_mode", e.target.checked ? "1" : "0");
                toast.success(e.target.checked ? "Letterhead Mode ON — prints will skip the company header and leave space for pre-printed stationery" : "Letterhead Mode OFF — prints include the full company header");
              }}
              data-testid="letterhead-toggle"
            />
            <span>Skip the printed company header and leave ~3cm blank for pre-printed letterhead stationery <span className="text-xs text-muted-foreground">(applies to this device only)</span></span>
          </label>
        </CardContent>
      </Card>

      {profile.vision && (
        <Card className="mt-6" data-testid="vision-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Eye className="h-4 w-4 text-primary" /> NTC Vision</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-slate-700" data-testid="profile-vision">{profile.vision}</p>
          </CardContent>
        </Card>
      )}

      {profile.md_message && (
        <Card className="mt-6" data-testid="md-message-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Quote className="h-4 w-4 text-primary" /> Managing Director's Message</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm italic text-slate-700" data-testid="profile-md-message">"{profile.md_message}"</p>
            <p className="mt-2 text-sm font-semibold">— {profile.md_name || "Managing Director"}</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="font-display">Edit NTC Profile</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <F k="name" label="Company Name" form={form} setForm={setForm} />
            <F k="regional_office" label="Regional Office (shown in the app header only — not printed)" form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Company Opening Date</Label>
                <Input type="date" value={form.opening_date ?? ""} onChange={(e) => setForm((f) => ({ ...f, opening_date: e.target.value }))} data-testid="profile-field-opening_date" />
              </div>
              <F k="md_name" label="Managing Director" form={form} setForm={setForm} />
            </div>
            <F k="md_mobile" label="MD Mobile No. (WhatsApp — day summaries open straight in this chat)" form={form} setForm={setForm} />
            <F k="md_message" label="Managing Director's Message" textarea form={form} setForm={setForm} />
            <F k="bill_terms" label="Bill Terms (one per line — printed on every bill after the bank line)" textarea form={form} setForm={setForm} />
            <F k="address" label="Address" textarea form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <F k="gstin" label="GSTIN" form={form} setForm={setForm} />
              <F k="pan" label="PAN" form={form} setForm={setForm} />
            </div>
            <F k="msme_number" label="MSME Number (UDYAM)" form={form} setForm={setForm} />
            <F k="vision" label="NTC Vision" textarea form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <F k="phone" label="Phone" form={form} setForm={setForm} />
              <F k="email" label="Email" form={form} setForm={setForm} />
            </div>
            <F k="website" label="Website" form={form} setForm={setForm} />
            <F k="branches" label="Branches (comma-separated)" form={form} setForm={setForm} />
            <div className="grid gap-2 rounded-md border p-3">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Branch Addresses (printed on that branch's LRs, challans & reminder letters)</Label>
              {String(form.branches || "").split(",").map((b) => b.trim()).filter(Boolean).map((b) => (
                <div key={b} className="grid gap-1">
                  <Label className="text-xs">{b} Branch Address</Label>
                  <Input
                    value={(form.branch_addresses || {})[b] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, branch_addresses: { ...(f.branch_addresses || {}), [b]: e.target.value } }))}
                    placeholder={`Full address of ${b} branch`}
                    data-testid={`branch-address-input-${b.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
            <div className="grid gap-2 rounded-md border p-3">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Authorised Signatory Signature (stamped on bills & certificates)</Label>
              {form.signature_image ? (
                <div className="flex items-center gap-3">
                  <img src={form.signature_image} alt="signature" className="h-12 rounded border bg-white object-contain p-1" data-testid="signature-preview" />
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, signature_image: "" }))} data-testid="signature-remove-btn">Remove</Button>
                </div>
              ) : (
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  data-testid="signature-upload-input"
                  onChange={uploadStamp("signature_image")}
                />
              )}
              <p className="text-xs text-muted-foreground">Upload a PDF, JPG or PNG scan (max 8MB) — the blank page margins are cropped automatically. It prints above the "Authorised Signatory" line on tax bills, COA certificates, money receipts and payment vouchers.</p>
            </div>
            <div className="grid gap-2 rounded-md border p-3">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Round Company Seal (printed beside the signature)</Label>
              {form.seal_image ? (
                <div className="flex items-center gap-3">
                  <img src={form.seal_image} alt="company seal" className="h-14 w-14 rounded border bg-white object-contain p-1" data-testid="seal-preview" />
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, seal_image: "" }))} data-testid="seal-remove-btn">Remove</Button>
                </div>
              ) : (
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  data-testid="seal-upload-input"
                  onChange={uploadStamp("seal_image")}
                />
              )}
              <p className="text-xs text-muted-foreground">Upload a PDF, JPG or PNG scan of the round seal (max 8MB) — blank margins are cropped automatically. It stamps beside the signature on the Bill Print.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F k="fleet_manager_name" label="Fleet Manager Name" form={form} setForm={setForm} />
              <F k="fleet_manager_whatsapp" label="Fleet Manager WhatsApp No." form={form} setForm={setForm} />
            </div>
            <F k="bank_name" label="Bank Name" form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <F k="account_no" label="Account No." form={form} setForm={setForm} />
              <F k="ifsc" label="IFSC" form={form} setForm={setForm} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-profile-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
